import { useParams, Link } from "react-router-dom"
import { useQuery } from "@apollo/client"
import { gql } from "~/__generated__"
import { ChevronLeft } from "lucide-react"
import { useState } from "react"
import { Button } from "~/ui/button"
import { JoinPlanModal } from "~/plans/join-plan-modal"
import { TablePageLayout } from "~/layouts/table-page-layout"
import { GraphqlError } from "~/ui/errors"
import { locationsPath } from "~/common/paths"
import { cn } from "~/common/cn"

const LOCATION_QUERY = gql(/* GraphQL */ `
  query LocationDetails($id: ID!) {
    node(id: $id) {
      ... on Location {
        id
        name
        status
        websiteUrl
        googleMapsUrl
        latitude
        longitude
        streetAddress
        streetAddress2
        city
        stateProvince
        postalCode
        country
        formattedAddress
        placeId
        administrativeAreaLevel1
        administrativeAreaLevel2
        neighborhood
        phone
        timeZone
        createdAt
        plans {
          edges {
            node {
              id
              title
              description
              datetime
              minPeople
              maxPeople
              planParticipations {
                edges {
                  node {
                    user {
                      id
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`)

export const LocationDetailsScreen = () => {
  const [joinPlanId, setJoinPlanId] = useState<string | null>(null)
  const { id } = useParams()
  const { data, error, loading } = useQuery(LOCATION_QUERY, {
    variables: { id: id! },
  })
  const { data: viewerData } = useQuery(
    gql(/* GraphQL */ `
      query ViewerForLocationDetails {
        viewer {
          id
        }
      }
    `)
  )

  if (loading) return null
  if (error) return <GraphqlError error={error} />

  const location = data?.node
  if (!location || location.__typename !== "Location") return <div>Location not found</div>

  return (
    <TablePageLayout>
      {/* Back Link */}
      <Link
        to={locationsPath({})}
        className={cn(
          "mb-4 inline-flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900 hover:underline"
        )}
      >
        <ChevronLeft className="h-4 w-4" /> Back to Locations
      </Link>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold">{location.name}</h1>
          <p className="mt-2 text-gray-600">Status: {location.status.toLowerCase()}</p>
        </div>

        <div className="space-y-4">
          {location.websiteUrl && (
            <p>
              <span className="font-semibold">Website: </span>
              <a
                href={location.websiteUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:underline"
              >
                Visit Website
              </a>
            </p>
          )}

          {location.googleMapsUrl && (
            <p>
              <span className="font-semibold">Google Maps: </span>
              <a
                href={location.googleMapsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:underline"
              >
                View on Google Maps
              </a>
            </p>
          )}

          {location.latitude && location.longitude && (
            <p>
              <span className="font-semibold">Coordinates: </span>
              {location.latitude}, {location.longitude}
            </p>
          )}

          <p>
            <span className="font-semibold">Created: </span>
            {new Date(location.createdAt).toLocaleDateString()}
          </p>
        </div>

        <div className="space-y-4">
          <h2 className="text-lg font-semibold">Plans at this Location</h2>
          {location.plans.edges.length > 0 ? (
            <div className="space-y-4">
              {location.plans.edges.map(({ node }) => {
                if (!node) return null
                return (
                  <div key={node.id} className="rounded-lg border p-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="font-medium">{node.title}</h3>
                        {node.description && (
                          <p className="mt-1 text-gray-600">{node.description}</p>
                        )}
                      </div>
                      <div className="flex gap-2">
                        {node.planParticipations.edges.some(
                          (edge) => edge.node?.user.id === viewerData?.viewer?.id
                        ) ? (
                          <Button variant="secondary" onClick={() => setJoinPlanId(node.id)}>
                            Leave Plan
                          </Button>
                        ) : (
                          <Button variant="default" onClick={() => setJoinPlanId(node.id)}>
                            Join Plan
                          </Button>
                        )}
                        <Link to={`/plans/${node.id}`} className="text-blue-600 hover:underline">
                          View Plan
                        </Link>
                      </div>
                    </div>
                    <div className="mt-2 text-sm text-gray-600">
                      <p>Date: {new Date(node.datetime).toLocaleString()}</p>
                      {node.minPeople && <p>Minimum People: {node.minPeople}</p>}
                      {node.maxPeople && <p>Maximum People: {node.maxPeople}</p>}
                    </div>
                  </div>
                )
              })}
            </div>
          ) : (
            <p className="text-gray-600">No plans scheduled at this location.</p>
          )}
        </div>
      </div>
      {joinPlanId && (
        <JoinPlanModal
          planId={joinPlanId}
          planTitle={
            location.plans.edges.find((edge) => edge.node?.id === joinPlanId)?.node?.title ?? ""
          }
          isOpen={true}
          onClose={() => setJoinPlanId(null)}
          isJoined={
            location.plans.edges
              .find((edge) => edge.node?.id === joinPlanId)
              ?.node?.planParticipations.edges.some(
                (edge) => edge.node?.user.id === viewerData?.viewer?.id
              ) ?? false
          }
        />
      )}
    </TablePageLayout>
  )
}
